import { useMutation, useQuery } from '@apollo/client';
import { toTestType } from '@risksmart-app/domain/src/types/consts/test-type';
import {
  GetControlTestOversightResultByIdDocument,
  InsertControlTestOversightResultDocument,
  Parent_Type_Enum,
  UpdateControlTestOversightResultApiDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import TestResultForm from 'src/pages/controls/update/forms/TestResultForm';
import type { TestResultFormFieldsData } from 'src/pages/controls/update/forms/testResultSchema';
import { defaultValues } from 'src/pages/controls/update/forms/testResultSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import type { UserOption } from 'src/schemas/global';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { evictField } from '@/utils/graphqlUtils';
import { oversightAssessmentResultsUrl } from '@/utils/urls';
type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  parentId: string;
  controlTestId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  controlIds?: string[];
  header?: string;
  isModalForm?: boolean;
  showAssessmentSelector?: boolean;
};

const ConnectedControlTestOversightResultForm: FC<Props> = (props) => {
  const navigate = useNavigate();
  const Id = props.id;
  let Ids: string[] = [];
  if (Id) {
    Ids.push(Id);
  }
  const { data, loading: loadingOversightTestResult } = useQuery(
    GetControlTestOversightResultByIdDocument,
    {
      variables: {
        Id: Id,
      },
      skip: !Id,
      fetchPolicy: 'no-cache',
    }
  );
  const testResult = data?.control_test_oversight_result[0];
  const { updateFiles } = useMultiParentFileUpdate();

  const [insert] = useMutation(InsertControlTestOversightResultDocument, {
    update: (cache) => {
      evictField(cache, 'control_test_oversight_result');
      evictField(cache, 'control');
      evictField(cache, 'oversight_assessment');
    },
  });
  //TODO: Update correct object.
  const [update] = useMutation(UpdateControlTestOversightResultApiDocument, {
    update: (cache) => {
      evictField(cache, 'control_test_oversight_result');
      evictField(cache, 'control');
      evictField(cache, 'oversight_assessment');
    },
  });

  const onSave = async (data: TestResultFormFieldsData) => {
    const { files, ...rest } = data;
    if (testResult) {
      const {
        ParentControlIds: _,
        OversightAssessmentId: __,
        ...updateData
      } = rest;
      const { data } = await update({
        variables: {
          object: {
            ...updateData,
            OriginalTimestamp: testResult.ModifiedAtTimestamp,
            Id: testResult.Id,
            ParentControlId: testResult.ParentControlId,
            CustomAttributeData: rest.CustomAttributeData || undefined,
            Submitter: rest.Submitter.value,
          },
        },
      });
      if (!data?.updateControlTestOversightResultApi?.Id) {
        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
    } else {
      const oversightAssessmentId =
        props.parentId || rest.OversightAssessmentId;
      if (!oversightAssessmentId) {
        throw new Error('An oversight assessment is required');
      }
      const result = await insert({
        variables: {
          ...rest,
          ControlIds: rest.ParentControlIds.map((p) => p.value),
          OversightAssessmentId: oversightAssessmentId,
          CustomAttributeData: rest.CustomAttributeData || undefined,
          Submitter: rest.Submitter.value,
        },
      });
      if (!result.data?.insertChildControlTestOversightResult?.Ids) {
        throw new Error('Id not found');
      }
      Ids = result.data?.insertChildControlTestOversightResult?.Ids;
    }
    if (!Ids) {
      throw new Error('Id not found');
    }

    await updateFiles({
      parentType: Parent_Type_Enum.ControlTestSecondLineResult,
      parentIds: Ids,
      originalFiles: testResult?.files.map((f) => f.file),
      selectedFiles: files,
    });
    if (props.navigateToResults) {
      navigate(oversightAssessmentResultsUrl(props.parentId));
    }
  };

  return (
    <div className={'pb-5'}>
      <TestResultForm
        {...props}
        renderTemplate={(renderProps) =>
          props.isModalForm ? (
            <ModalBodyWrapper {...renderProps} />
          ) : (
            <PageWrapper {...renderProps} />
          )
        }
        defaultValues={{
          ...defaultValues,
        }}
        assessmentMode={'compliance_monitoring_assessment'}
        values={{
          ...defaultValues,
          ...testResult,
          TestType: toTestType(testResult?.TestType),
          files: testResult?.files.map((rf) => rf.file) ?? [],
          Submitter: testResult?.Submitter
            ? { value: testResult.Submitter, type: 'user' }
            : (undefined as unknown as UserOption),
          ParentControlIds: testResult
            ? [{ value: testResult.ParentControlId }]
            : props.controlIds
              ? props.controlIds.map((c) => ({
                  value: c,
                }))
              : [],
        }}
        onSave={onSave}
        onDismiss={props.onDismiss}
        readOnly={props.readonly || loadingOversightTestResult}
        disableControlSelect={props.id !== undefined}
      />
    </div>
  );
};

export default ConnectedControlTestOversightResultForm;
